import { vec2, mat2d, glMatrix } from "gl-matrix";
import { Big } from "big.js";
import { limitAngle, rotateMatrixAroundPoint, getRadiansFromAngle } from "../math/Angles";
import { TransformTypes, Positions, Mode, SCALE_MIN } from "../../constants/TransformerConstants";
import { FindSnapMatches } from "../Snapping/SnappingUtils";
import { updateToolMatrix } from "./tool";
import { getScaleTranslation } from "./translation";

const { MOVE, ROTATE, RESIZE } = TransformTypes;
const { TOP_MIDDLE, LEFT_MIDDLE, RIGHT_MIDDLE, BOTTOM_MIDDLE } = Positions;

glMatrix.setMatrixArrayType(Array);

/**
 * @typedef { import("./types").tool } tool
 * @typedef { import("./types").action } action
 * @typedef { import("./types").Mode } mode
 * @typedef {import("@/types/MathTypes.mjs").Coordinates} Coordinates
 * @typedef { import("./types").ConstrainArea } ConstrainArea
 */

/**
 * Gets the delta relative to a matrix
 * @param {mat2d} baseMatrix
 * @param {vec2 | Array<number>} deltaVector
 */
export const getLocalDelta = (baseMatrix, deltaVector) => {
  const inv = mat2d.invert(mat2d.create(), baseMatrix);

  // delta in local space.
  const deltaU = inv[0] * deltaVector[0] + inv[2] * deltaVector[1];
  const deltaV = inv[3] * deltaVector[1] + inv[1] * deltaVector[0];

  return [deltaU, deltaV];
};

// todo: type this. This could probably be more programmatic
// gets diff from tool and comparison element for the top left and bottom right corners
export const getLocalToolCornerDiffs = ({
  tool,
  toolStart,
  yOffset,
  comparison,
  allCorners = false,
}) => {
  const elementMatrix = updateToolMatrix(comparison);
  const toolMatrix = updateToolMatrix({
    ...tool,
    x: toolStart.x,
    // remove yOffset to make relative to canvas rather than whole design
    y: toolStart.y - yOffset,
  });

  const rotationMatrix = [...elementMatrix.slice(0, 4), 0, 0];
  const inverseRotationMatrix = mat2d.invert(mat2d.create(), rotationMatrix);

  // top left and bottom right corners of each for comparison
  const elementTL = vec2.transformMat2d(vec2.create(), [0, 0], elementMatrix);
  const elementBR = vec2.transformMat2d(vec2.create(), [comparison.w, comparison.h], elementMatrix);
  const toolMatTL = vec2.transformMat2d(vec2.create(), [0, 0], toolMatrix);
  const toolMatBR = vec2.transformMat2d(vec2.create(), [toolStart.w, toolStart.h], toolMatrix);

  // relative diffs in canvas space
  const TLDiff = vec2.sub(vec2.create(), toolMatTL, elementTL);
  const BRDiff = vec2.sub(vec2.create(), toolMatBR, elementBR);

  // constrain rotated back to local space
  const localTLDiff = vec2.transformMat2d(vec2.create(), TLDiff, inverseRotationMatrix);
  const localBRDiff = vec2.transformMat2d(vec2.create(), BRDiff, inverseRotationMatrix);

  let localTRDiff;
  let localBLDiff;

  if (allCorners) {
    const elementTR = vec2.transformMat2d(vec2.create(), [comparison.w, 0], elementMatrix);
    const elementBL = vec2.transformMat2d(vec2.create(), [0, comparison.h], elementMatrix);
    const toolMatTR = vec2.transformMat2d(vec2.create(), [toolStart.w, 0], toolMatrix);
    const toolMatBL = vec2.transformMat2d(vec2.create(), [0, toolStart.h], toolMatrix);

    // relative diffs in canvas space
    const TRDiff = vec2.sub(vec2.create(), toolMatTR, elementTR);
    const BLDiff = vec2.sub(vec2.create(), toolMatBL, elementBL);

    // constrain rotated back to local space
    localTRDiff = vec2.transformMat2d(vec2.create(), TRDiff, inverseRotationMatrix);
    localBLDiff = vec2.transformMat2d(vec2.create(), BLDiff, inverseRotationMatrix);
  }

  return {
    localTLDiff,
    localTRDiff,
    localBLDiff,
    localBRDiff,
    rotationMatrix,
    inverseRotationMatrix,
  };
};

/**
 * Wraps transform handlers into a single function. Chooses and calls the correct handler based on the transform type set in the action.
 * @param {Object} props
 * @param {action} props.action - The action object containing the transformation data.
 * @param {number=} props.aspectRatio - Aspect ratio to constrain the tool to.
 * @param {ConstrainArea} props.constrainArea - Area to constrain the tool around or within.
 * @param {number} props.constrainAngle - The angle to constrain the rotations by. Defaults to 15.
 *  @param {Coordinates=} props.diffPoint - The distance moved from the controlPoint.
 * @param {mode} props.mode - Represents the mode of the transformer.
 * @param {boolean} props.altTransform - Whether or not to transform modified by the alt key.
 * @param {number} props.minHeight - The minimum height of the tool.
 * @param {number} props.minWidth - The minimum width of the tool.
 * @param {tool} props.tool - The tool object to be transformed.
 *
 * @returns {tool}
 */

export const handleTransform = ({
  tool: prevTool,
  action,
  aspectRatio,
  mode,
  constrainAngle,
  constrainArea,
  altTransform,
  minHeight,
  minWidth,
  diffPoint,
}) => {
  const props = {
    tool: { ...prevTool },
    action,
    altTransform,
    aspectRatio,
    constrainAngle,
    constrainArea,
    mode,
    minHeight,
    minWidth,
    diffPoint: diffPoint ?? action.diffPoint,
  };
  return {
    [MOVE]: moveHandler,
    [ROTATE]: rotateHandler,
    [RESIZE]: resizeHandler,
  }[action.type](props);
};

/**
 *  Handles the move transform type event.
 * @param {Object} props
 * @param {tool} props.tool - The tool object to be transformed.
 * @param {action} props.action - The transform action object
 * @param {ConstrainArea} props.constrainArea - Area to constrain the tool around
 * @returns {tool} - Returns the updated tool.
 */

export const moveHandler = ({ tool, action, constrainArea, diffPoint }) => {
  const { toolStart, snapPoints, yOffset } = action;
  let newX = toolStart.x + diffPoint[0];
  let newY = toolStart.y + diffPoint[1];

  // constrain move around an area
  if (constrainArea?.around) {
    const { around } = constrainArea;

    const { localTLDiff, localBRDiff, rotationMatrix, inverseRotationMatrix } =
      getLocalToolCornerDiffs({
        tool,
        toolStart,
        yOffset,
        comparison: around,
      });

    // apply delta, but as though it were rotated to the element's orientation
    const relativeDelta = vec2.transformMat2d(vec2.create(), diffPoint, inverseRotationMatrix);
    vec2.add(localTLDiff, localTLDiff, relativeDelta);
    vec2.add(localBRDiff, localBRDiff, relativeDelta);

    // find the applicable offsets in local space (much simpler here than when out and rotated)
    const overage = [0, 0];

    if (localTLDiff[0] > 0) overage[0] += localTLDiff[0];
    if (localTLDiff[1] > 0) overage[1] += localTLDiff[1];

    if (localBRDiff[0] < 0) overage[0] += localBRDiff[0];
    if (localBRDiff[1] < 0) overage[1] += localBRDiff[1];

    // rotate back
    const canvasSpaceOffset = vec2.transformMat2d(vec2.create(), overage, rotationMatrix);
    const inverseCanvasOffset = vec2.sub(vec2.create(), [0, 0], canvasSpaceOffset);

    // apply the overage in reverse
    newX += inverseCanvasOffset[0];
    newY += inverseCanvasOffset[1];
  }

  if (snapPoints?.x.length > 0) {
    [newX, newY] = FindSnapMatches({ ...action, newX, newY, tool });
  }

  if (action.constrain) {
    if (action.constrainAxis === "H") {
      newY = tool.y;
    } else {
      newX = tool.x;
    }
  }

  return { ...tool, x: newX, y: newY };
};

/**
 * Calculates the scale factor for an axis based on the given value and delta.
 *
 * @param {number} value - The original value of the axis.
 * @param {number} delta - The change in value for the axis.
 * @returns {number} - The calculated scale factor for the axis.
 */
const scaleAxis = (value, delta) => (value + delta) / value;

/**
 * Scales the given value vector by the provided scales.
 *
 * @param {vec2 | Array<number>} values - The values to be scaled.
 * @param {vec2 | Array<number>} scales - The scales to apply.
 * @returns {vec2} - The scaled vector.
 */
export const scaleAxes = (values, scales) => [
  scaleAxis(values[0], scales[0]),
  scaleAxis(values[1], scales[1]),
];

const getScaleMinimums = (action, minWidth, minHeight) => {
  const minScaleX = minWidth ? new Big(minWidth).div(action.toolStart.w).toNumber() : SCALE_MIN;
  const minScaleY = minHeight ? new Big(minHeight).div(action.toolStart.h).toNumber() : SCALE_MIN;
  return [minScaleX, minScaleY];
};

const handleAltScale = (vector, action, minHeight, minWidth) => {
  let [x, y] = vector;

  // double current scale
  x = 1 + (x - 1) * 2;
  y = 1 + (y - 1) * 2;

  return [x, y];
};

/**
/**
 * Constrains the resize vector based on the action constraints, mode, and minimum size.
 *
 * @param {Array<number>} vector - The original vector to be constrained.
 * @param {Object} action - The transform action object containing constraints.
 * @param {boolean} scaleXOnly - Flag indicating if only the X axis should be scaled.
 * @param {boolean} scaleYOnly - Flag indicating if only the Y axis should be scaled.
 * @param {boolean} altTransform - Flag indicating if alt-based transform is active.
 * @param {mode} mode - The mode of transformation, e.g., single or multi.
 * @param {number} [minHeight] - The minimum allowed height (optional).
 * @param {number} [minWidth] - The minimum allowed width (optional).
 * @returns {Array<number>} - The constrained vector for X and Y axes.
 */
export const constrainResizeVector = (
  vector,
  action,
  scaleXOnly,
  scaleYOnly,
  altTransform,
  mode,
  minHeight,
  minWidth
) => {
  const isScale = mode === Mode.MULTI || action.constrain;

  const [baseX, baseY] = vector;
  let x = scaleYOnly ? (action.constrain ? baseY : 1) : baseX;
  let y = scaleXOnly ? (action.constrain ? baseX : 1) : baseY;

  if (isScale || altTransform) {
    const [minScaleX, minScaleY] = getScaleMinimums(action, minHeight, minWidth);

    if (altTransform) {
      [x, y] = handleAltScale([x, y]);
    }

    if (new Big(x).lt(minScaleX)) x = minScaleX;
    if (new Big(y).lt(minScaleY)) y = minScaleY;
  }

  if (isScale) {
    const maxVal = Math.max(x, y);
    x = maxVal;
    y = maxVal;
  }

  return [x, y];
};

/**
 * Constrains the delta to ensure the tool does not resize below minimum dimensions.
 *
 * @param {Object} action - The transform action object containing control and registration points.
 * @param {Coordinates} delta - The delta vector representing the change in position.
 * @param {number} minHeight - The minimum height allowed for the tool.
 * @param {number} minWidth - The minimum width allowed for the tool.
 * @returns {Coordinates} - The constrained delta values for U and V.
 */
export const constrainMinimum = (action, delta, minHeight, minWidth) => {
  const regOffset = vec2.subtract(vec2.create(), action.controlPoint, action.regPoint);

  // Calculate current scale factors
  const [x, y] = scaleAxes(regOffset, delta);

  const [minScaleX, minScaleY] = getScaleMinimums(action, minWidth, minHeight);

  // Apply minimum constraints
  const constrainedX = x < minScaleX || Number.isNaN(x) || !Number.isFinite(x) ? minScaleX : x;
  const constrainedY = y < minScaleY || Number.isNaN(y) || !Number.isFinite(y) ? minScaleY : y;

  // Calculate the constrained delta, or the original delta minus
  const constrainedDeltaU = new Big(constrainedX).times(regOffset[0]).minus(regOffset[0]);
  const constrainedDeltaV = new Big(constrainedY).times(regOffset[1]).minus(regOffset[1]);

  return [constrainedDeltaU.toNumber(), constrainedDeltaV.toNumber()];
};

/**
 * Maintains the original vector's orientation after a scale or edit.
 * @param {vec2} vector1
 * @param {vec2} vector2
 * @return {vec2}
 */
const maintainOrientation = (vector1, vector2) => {
  const newVec = vec2.clone(vector2);

  if (Math.sign(vector1[0]) !== Math.sign(newVec[0])) newVec[0] *= -1;
  if (Math.sign(vector1[1]) !== Math.sign(newVec[1])) newVec[1] *= -1;

  return newVec;
};

/**
 *
 * @param {Object} action
 * @param {Coordinates} delta
 * @param {number} aspectRatio
 * @returns {Coordinates}
 */
export const constrainAspectRatio = (action, delta, aspectRatio) => {
  // todo: just insert this
  const regOffset = vec2.subtract(vec2.create(), action.controlPoint, action.regPoint);

  // scale x to the aspect ratio since the ratio is w/h (as in a how big w is in relation to h)
  const lockedOffset = vec2.clone(regOffset);
  lockedOffset[0] = new Big(regOffset[1]).times(aspectRatio).toNumber();

  // maintains orientation
  const orientedOffset = maintainOrientation(regOffset, lockedOffset);

  // unit vec
  vec2.normalize(orientedOffset, orientedOffset);

  // Project the delta onto this adjusted direction
  const movementLength = vec2.dot(delta, orientedOffset);

  // effectively a slice of the original line with the length of the delta (to be applied later)
  const constrainedDelta = vec2.scale(vec2.create(), orientedOffset, movementLength);

  return constrainedDelta;
};

/**
 * Constrains the delta depending on supplied constraints
 * @param {Object} action
 * @param {Coordinates} delta
 * @param {{
 *  minHeight: number,
 *  minWidth: number,
 *  aspectRatio?: number,
 * }} constraints
 * @returns {Coordinates}
 */
const handleDeltaConstraints = (action, delta, { minHeight = 0, minWidth = 0, aspectRatio }) => {
  let newDelta = vec2.fromValues(...delta);

  if (aspectRatio) newDelta = constrainAspectRatio(action, newDelta, aspectRatio);

  newDelta = constrainMinimum(action, newDelta, minHeight, minWidth);

  return newDelta;
};

/**
 * Gets min size for resize handler. Handles special cases with around constraints if supplied.
 * @param {object} props
 * @param {action} props.action
 * @param {boolean} props.altTransform
 * @param {number=} props.aspectRatio
 * @param {ConstrainArea} props.constrainArea
 * @param {number=} props.minHeight
 * @param {number=} props.minWidth
 * @returns
 */
export const getConstraintAreaMins = ({
  action,
  altTransform,
  aspectRatio,
  constrainArea,
  minHeight,
  minWidth,
}) => {
  const base = [minWidth, minHeight];

  if (constrainArea?.around) {
    const { around } = constrainArea;
    base[0] = around?.w;
    base[1] = around?.h;

    // maintain aspect ratio
    if (aspectRatio) {
      // finds the available min area by getting the element h/w plus the diff between both opposite corners (in local space)
      const { toolStart, handle, yOffset } = action;

      const baseEdge = new Big(base[0] > base[1] ? base[0] : base[1]);

      // todo: probably some temp rather than sending back to base
      if (baseEdge.eq(base[0])) {
        base[1] = baseEdge.div(aspectRatio).toNumber();
      } else if (baseEdge.eq(base[1])) {
        base[0] = baseEdge.times(aspectRatio).toNumber();
      }

      const { localBLDiff, localBRDiff, localTLDiff, localTRDiff } = getLocalToolCornerDiffs({
        tool: toolStart,
        toolStart,
        yOffset,
        comparison: around,
        allCorners: true,
      });

      // opposite corner diffs
      const oppositeCornerDiffs = {
        [Positions.TOP_LEFT]: localBRDiff,
        [Positions.TOP_RIGHT]: localBLDiff,
        [Positions.BOTTOM_RIGHT]: localTLDiff,
        [Positions.BOTTOM_LEFT]: localTRDiff,
      };

      if (altTransform) {
        const diff = Object.values(oppositeCornerDiffs).reduce((result, diff, index) => {
          if (!index) return diff;

          const resultLength = vec2.length(result);
          const currentLength = vec2.length(diff);

          return currentLength > resultLength ? result : diff;
        }, []);

        const minW = new Big(toolStart.w).minus(new Big(Math.abs(diff[0])).times(2));
        const minH = new Big(toolStart.h).minus(new Big(Math.abs(diff[1])).times(2));

        const result = [minW.toNumber(), minH.toNumber()];

        base[0] = result[0];
        base[1] = result[1];
      } else {
        const oppositeDiff = oppositeCornerDiffs[handle];

        const minW = new Big(around.w).plus(Math.abs(oppositeDiff[0]));
        const minH = new Big(around.h).plus(Math.abs(oppositeDiff[1]));

        const heightFromMinW = minW.div(aspectRatio);
        const widthFromMinH = minH.times(aspectRatio);

        // as in bound by the height calculated from minW
        const heightBound = heightFromMinW.gt(minH);

        const result = [
          (heightBound ? minW : widthFromMinH).toNumber(),
          (heightBound ? heightFromMinW : minH).toNumber(),
        ];

        base[0] = result[0];
        base[1] = result[1];
      }
    }
  }

  return base;
};

/**
 *  Handles the resize transform type event.
 * @param {Object} props
 * @param {action} props.action - The transform action object.
 * @param {number=} props.aspectRatio - Aspect ratio to constrain the tool to.
 * @param {ConstrainArea} props.constrainArea - Area to constrain the tool around.
 * @param {Coordinates} props.diffPoint - The distance moved from the controlPoint
 * @param {mode} props.mode - Represents the mode of the transformer.
 * @param {boolean} props.altTransform - Whether or not to transform modified by the alt key.
 * @param {number} props.minHeight - The minimum height of the tool.
 * @param {number} props.minWidth - The minimum width of the tool.
 * @param {tool} props.tool - The tool object to be transformed.
 * @returns {tool} - Returns the updated tool.
 */

const resizeHandler = ({
  tool: prevTool,
  action,
  mode,
  altTransform,
  minHeight,
  minWidth,
  constrainArea,
  aspectRatio,
  diffPoint,
}) => {
  const { toolStart, handle } = action;
  const tool = { ...prevTool };

  const scaleXOnly = [LEFT_MIDDLE, RIGHT_MIDDLE].includes(handle);
  const scaleYOnly = [TOP_MIDDLE, BOTTOM_MIDDLE].includes(handle);

  // diff from the registration point (non moving opposite anchor) and the control point (handle being manipulated at start)
  const regOffset = vec2.subtract(vec2.create(), action.controlPoint, action.regPoint);

  const [deltaU, deltaV] = getLocalDelta(toolStart.matrix, diffPoint);

  const [constrainedMinWidth, constrainedMinHeight] = getConstraintAreaMins({
    action,
    altTransform,
    aspectRatio,
    constrainArea,
    minHeight,
    minWidth,
  });

  const constrainedDeltas = handleDeltaConstraints(action, [deltaU, deltaV], {
    aspectRatio: scaleXOnly || scaleYOnly ? undefined : aspectRatio,
    minHeight: constrainedMinHeight,
    minWidth: constrainedMinWidth,
  });

  // scale the difference across the rect using constrained delta
  const scaledAxes = scaleAxes(regOffset, constrainedDeltas);

  // constraint scaling
  const [x, y] = constrainResizeVector(
    scaledAxes,
    action,
    scaleXOnly,
    scaleYOnly,
    altTransform,
    mode,
    constrainedMinWidth,
    constrainedMinHeight
  );

  const w = Math.round(toolStart.w * x);
  const h = Math.round(toolStart.h * y);

  tool.w = Math.abs(w < 1 ? 1 : w);
  tool.h = Math.abs(h < 1 ? 1 : h);

  // these are a scale of the reg/control
  const xyVec = vec2.fromValues(x, y);

  const symmetricTransform = altTransform
    ? {
        dimensionDiff: vec2.subtract(vec2.create(), [tool.w, tool.h], [toolStart.w, toolStart.h]),
        regOffset,
        scaleXOnly,
        scaleYOnly,
      }
    : undefined;

  let {
    xyOffset,
    transformMatrix: tM,
    regOffsetResized,
  } = getScaleTranslation(
    action.regPoint,
    toolStart.matrix,
    xyVec,
    { ...tool },
    symmetricTransform
  );

  let [offsetX, offsetY] = xyOffset;

  if (
    action.snapPoints?.x.length > 0 &&
    !altTransform &&
    (tool.a === undefined || tool.a % 90 === 0) && // only snap to center handle if the angle is a multiple of 90
    x !== SCALE_MIN &&
    y !== SCALE_MIN
  ) {
    const snapConfig = {
      ...action,
      handle,
      tool,
      newX: offsetX,
      newY: offsetY,
    };

    // reassign offsets with snap shift
    regOffsetResized = FindSnapMatches(snapConfig);
    [offsetX, offsetY] = regOffsetResized;
  }

  tool.x = offsetX;
  tool.y = offsetY;

  const transM = mat2d.create();
  mat2d.translate(transM, transM, regOffsetResized);
  mat2d.multiply(tM, transM, tM);

  tool.matrix = tM;

  return tool;
};

/**
 *  Handles the rotate transform type event.
 * @param {Object} props
 * @param {tool} props.tool - The tool object to be transformed.
 * @param {action} props.action - The transform action object.
 * @param {mode} props.mode - Represents the mode of the transformer.
 * @param {number} props.constrainAngle - The angle to constrain the rotation to.
 * @returns {tool} - Returns the updated tool.
 */

const rotateHandler = ({ tool: prevTool, action, mode, constrainAngle, diffPoint }) => {
  const { toolStart, constrain } = action;
  const tool = { ...prevTool };
  const startLoc = vec2.subtract(vec2.create(), action.startLocal, action.regPoint);

  const diffLoc = vec2.clone(startLoc);
  vec2.add(diffLoc, diffLoc, vec2.fromValues(diffPoint[0], diffPoint[1]));

  const rads = Math.atan2(diffLoc[1], diffLoc[0]) - Math.atan2(startLoc[1], startLoc[0]);

  let angle = (180 / Math.PI) * rads;

  angle = Math.round(angle + toolStart.a);

  if (constrain) {
    angle = Math.round(angle / parseInt(constrainAngle, 10)) * parseInt(constrainAngle, 10);
  }

  if (mode === Mode.MULTI) {
    tool.r = rads;
  }

  tool.a = limitAngle(angle);

  return tool;
};
